import React, { Component, ReactElement } from 'react';
import { FormikActions } from 'formik';
import { Mutation } from '@apollo/client/react/components';

import {
  JobPostingAndApplicationQuery,
  StarJobApplicationDocument,
  StarJobApplicationMutation,
  StarJobApplicationMutationFn,
  StarJobApplicationMutationVariables,
  UpdateJobAppStatusDocument,
  UpdateJobAppStatusMutation,
  UpdateJobAppStatusMutationFn,
  UpdateJobAppStatusMutationVariables
} from '@interfaces/graphql-typings';
export type JobApplication = Omit<
  NonNullable<JobPostingAndApplicationQuery['jobApplication']>,
  'jobPosting'
> & {
  jobPosting: Pick<
    NonNullable<JobPostingAndApplicationQuery['jobApplication']>['jobPosting'],
    'id' | 'title'
  >;
};
export type FormValues = { message: string };
export interface HandleMutations {
  handleInterviewed: (
    values: FormValues,
    actions: FormikActions<FormValues>
  ) => Promise<JobApplication | void>;
  handleRejected: (
    values: FormValues,
    actions: FormikActions<FormValues>
  ) => Promise<JobApplication | void>;
  handleStarred: () => void;
}
interface IProps {
  children: (props: HandleMutations & { isFetching: boolean }) => ReactElement;
  jobApplication: JobApplication;
}

class JobApplicationShowMutationsContainer extends Component<IProps, { isFetching: boolean }> {
  constructor(props: IProps) {
    super(props);
    this.state = { isFetching: false };
    this.toggleFetching = this.toggleFetching.bind(this);
    this.handleInterviewStatusChange = this.handleInterviewStatusChange.bind(this);
    this.handleStarred = this.handleStarred.bind(this);
  }

  toggleFetching(condition: boolean): void {
    this.setState({ isFetching: condition });
  }

  handleInterviewStatusChange(
    mutation: UpdateJobAppStatusMutationFn,
    status: UpdateJobAppStatusMutationVariables['status']
  ): (values: FormValues, actions: FormikActions<FormValues>) => Promise<void | JobApplication> {
    return (
      values: FormValues,
      actions: FormikActions<FormValues>
    ): Promise<void | JobApplication> => {
      const { jobApplication } = this.props;
      this.toggleFetching(true);
      return mutation({
        variables: { jobApplicationId: jobApplication.id, message: values.message, status: status }
      })
        .then((): void => {
          this.toggleFetching(false);
        })
        .catch((): void => {
          actions.setSubmitting(false);
        });
    };
  }

  handleStarred(mutation: StarJobApplicationMutationFn): () => void {
    return (): void => {
      const { jobApplication } = this.props;
      this.toggleFetching(true);
      mutation({
        variables: { jobApplicationId: jobApplication.id, starredBoolean: !jobApplication.starred }
      }).then((): void => this.toggleFetching(false));
    };
  }

  render(): React.ReactElement {
    const { children } = this.props;
    const { isFetching } = this.state;

    return (
      <Mutation<StarJobApplicationMutation, StarJobApplicationMutationVariables>
        mutation={StarJobApplicationDocument}
      >
        {(starJobApplication): React.ReactElement => (
          <Mutation<UpdateJobAppStatusMutation, UpdateJobAppStatusMutationVariables>
            mutation={UpdateJobAppStatusDocument}
          >
            {(updateJobApplication): React.ReactElement => {
              const handleInterviewed = this.handleInterviewStatusChange(
                updateJobApplication,
                'interviewed'
              );
              const handleRejected = this.handleInterviewStatusChange(
                updateJobApplication,
                'rejected'
              );

              return children({
                handleInterviewed,
                handleRejected,
                handleStarred: this.handleStarred(starJobApplication),
                isFetching: isFetching
              });
            }}
          </Mutation>
        )}
      </Mutation>
    );
  }
}

export default JobApplicationShowMutationsContainer;
